import PostRequest from "csi/comm/PostRequest";
import { getNodeSearchUnresolvedUrlPaths } from "csi/service/nodeService";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache, IDataStore } from "csi/typings";
import { arrayMove } from "csi/utility/arrayUtility";
import Search from "csi/xml/Search";
import { action, makeObservable, observable, runInAction } from "mobx";

class NodeDataStore implements IDataStore<C8Node> {
	/** force the specified node to the front of the resulted array */
	private readonly _forceNodeToFrontUrl: string;
	private readonly _search: Search;
	private readonly _sharedCache: boolean;
	private _bHasAllRefs: boolean;
	@observable private _urls: cnl[];
	@observable private _totalCount: number;

	/** cache to hold results */
	readonly cache: ICache;

	/** the total count of results that satisfy the store query which could be more than the system query limit */
	get totalCount(): number {
		return this._totalCount || this.total;
	}

	/** indicates if results are in process of being loaded */
	get isLoading(): boolean {
		return !this._urls;
	}

	/** search of this store */
	get search(): Search {
		return this._search;
	}

	/** total number of results loaded, if known */
	get total(): number | undefined {
		const { _bHasAllRefs, _search, _totalCount, _urls } = this;

		if (_bHasAllRefs || !_urls) {
			return _urls?.length;
		}

		const max = _search?.getMaxResultSize();
		return max ? Math.min(_totalCount, max) : _totalCount;
	}

	/** result urls, if known */
	get urls(): cnl[] | undefined {
		return this._urls;
	}

	/** return item at given index */
	get = (index: number): C8Node => this.cache.get(this._urls?.[index]);

	/** identity function */
	getIdentity = (node: C8Node): string => node.$URL;

	/** return index of item with given identity value */
	getIdentityIndex = (id: string): number | undefined => {
		return this._urls?.indexOf(id);
	};

	/** return identity of next (or previous) item in the store relative to item with given identity value */
	getNextIdentity = (id: string, previous?: boolean, defaultToFirst?: boolean): string | undefined => {
		const { get, getIdentityIndex, total } = this;
		let index = getIdentityIndex(id);

		if ((index >= 0) && (index < total)) {
			index += (previous ? -1 : 1);
		}

		if ((index === undefined) || (index < 0) || (index === total)) {
			if (defaultToFirst) {
				index = 0;
			} else {
				return undefined;
			}
		}

		const item = get(index);
		return item.$URL;
	};

	/** issue request to get nodes for given range, returning total number of results */
	load = async (start?: number, end?: number): Promise<number> => {
		const { cache, _forceNodeToFrontUrl, _search, _sharedCache, _urls } = this;
		const supportsPaging = _search?.supportsPaging() && (start !== undefined) && (end !== undefined);
		let request: PostRequest;

		// _bHasAllRefs can be false, true or undefined. It is important here to match exactly false value and not undefined
		if (!_urls || (this._bHasAllRefs === false)) {
			if (supportsPaging) {
				// add parameters for paging and getting list of all references back
				_search.setPageLimits(start + 1, end + 1, true);
			}

			request = _search.getRequest();
		} else if (supportsPaging) {
			// we have all result urls, so convert request to one that returns nodes for urls in desired range
			const urls = _urls.slice(start, end + 1);

			if (_sharedCache) {
				const paths: string[] = _search.getDependencyPaths();
				const [unresolvedUrls, unresolvedPaths] = getNodeSearchUnresolvedUrlPaths(cache, urls, paths);
				request = _search.getNodesRequest(unresolvedUrls, unresolvedPaths);
			} else {
				request = _search.getNodesRequest(urls);
			}
		}

		if (request) {
			// ensure standard search parameters are on request to support paging and access control
			request.addNodeCrewParameters();

			// make request and cache results
			const response = await request.go();

			runInAction(() => {
				cache.cacheResponse(response);

				if (!_urls) {
					this._totalCount = response.getTotalCount();
					this._bHasAllRefs = response.hasCompleteResultRefs();

					if (supportsPaging) {
						this._urls = response.getCompleteRefs();
					} else {
						this._urls = response.getResultUrls();
					}
				} else if (!this._bHasAllRefs) {
					this._urls = this._urls.concat(response.getResultUrls());
				}

				if (_forceNodeToFrontUrl) {
					this.move(this.getIdentityIndex(_forceNodeToFrontUrl), 0);
				}
			});
		}

		return this.total;
	};

	/** move item from one index to another */
	@action move = (from: number, to: number): void => {
		arrayMove(this._urls, from, to);
	};

	/** remove item from store */
	@action remove = (id: string): void => {
		const { getIdentityIndex, _urls } = this;
		const index = getIdentityIndex(id);

		if (index >= 0) {
			_urls.splice(index, 1);
			--this._totalCount;
		}
	};

	/** return promise resolving to resolved urls */
	resolveUrls = async (): Promise<cnl[]> => {
		const { load, _urls } = this;

		if (!_urls) {
			await load(0, 0);
		}

		return this._urls;
	};

	constructor(search: Search, cache?: ICache, forceNodeToFrontUrl?: string) {
		makeObservable(this);

		if (!search) {
			this._urls = [];
		}

		this._forceNodeToFrontUrl = forceNodeToFrontUrl;
		this._search = search;
		this._sharedCache = !!cache;
		this.cache = cache || new WorkingCache();
	}
}

export default NodeDataStore;